/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components
import Grid from "@mui/material/Grid";
// Material Dashboard 2 PRO React components
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import {Form, Formik} from "formik";
import React from "react";
import checkInitialValues from "./schemas/checkIn/initialValues";
import paymentInitialValues from "./schemas/payment/initialValues";
import MDBox from "../../../components/MDBox";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";
import useCheckIn from "./useCheckIn";
import BaseLayout from "../../../components/BaseLayout";
import CheckInForm from "./components/CheckInForm/CheckInForm";
import {API_URL} from "../../../config";
import FormGroup from "@mui/material/FormGroup/FormGroup";
import MDInput from "../../../components/MDInput";
import DataTable from "../../../components/DataTable/DataTable";
import moment from "moment";
import Icon from "@mui/material/Icon";
import colors from "../../../assets/theme-dark/base/colors";
import Message from "../../../components/Message/Message";
import NewPaymentForm from "../../../components/Forms/NewPaymentForm/NewPaymentForm";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// Settings page components

const CheckIn = () => {

    const {selectedMember, checkValidations, checkForm, paymentValidations, paymentForm, isLoading, members, handleCheckIn, handleCheckMembership, handlePayment, actions, filters, setFilters, renderStatusChip, shouldRefresh, sessionState} = useCheckIn();

    return (
        <BaseLayout>
            <Grid container justifyContent="center">
                <Grid item xs={12} lg={12}>
                    {
                        !sessionState.isRunning &&
                        <MDBox mb={3}>
                            <Message title="No Active Session" message="Heads up, there isn't an active session, some functionality will be disabled. Contact the manager for support." icon="warning" />
                        </MDBox>
                    }
                    <MDBox mb={3}>
                        <Card id="check-in" sx={{overflow: "visible"}}>
                            {
                                isLoading ?
                                    <MDBox mt={2} mx={2} p={3} display="flex" justifyContent="center"
                                           alignItems="center">
                                        <CircularProgress color="secondary" style={{overflow: 'hidden'}}/>
                                    </MDBox> :
                                    <Formik
                                        initialValues={checkInitialValues}
                                        validationSchema={checkValidations}
                                        onSubmit={handleCheckMembership}
                                    >
                                        {({values, errors, touched, isSubmitting}) => (
                                            <Form id={checkForm.formId} autoComplete="off">
                                                <MDBox p={3}>
                                                    <Grid container spacing={3} alignItems="center">
                                                        <Grid item xs={12} md={3}>
                                                            <MDTypography variant="h5">Member Check In</MDTypography>
                                                            <MDTypography variant="body2">New member arrived?</MDTypography>
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <CheckInForm members={members} formData={{
                                                                values,
                                                                touched,
                                                                formField: checkForm.formField,
                                                                errors
                                                            }}/>
                                                        </Grid>
                                                        <Grid item xs={12} md={3}>
                                                            <MDButton
                                                                disabled={isSubmitting}
                                                                variant="gradient"
                                                                color="secondary"
                                                                type="submit"
                                                                size="large"
                                                                fullWidth
                                                            >
                                                                {isSubmitting &&
                                                                <CircularProgress size={18} color="white"
                                                                                  sx={{marginRight: 1}}/>}
                                                                Check Membership
                                                            </MDButton>
                                                        </Grid>
                                                    </Grid>
                                                </MDBox>
                                            </Form>
                                        )}
                                    </Formik>
                            }
                        </Card>
                    </MDBox>
                    {
                        selectedMember &&
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                <MDBox mb={3}>
                                    <Card id="member-details">
                                        <MDBox p={3}>
                                            <MDBox>
                                                <MDTypography variant="h5" sx={{marginBottom: 4}} fontWeight="medium">Member Details</MDTypography>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} lg={6}>
                                                        <MDBox mb={2} display="flex" alignItems="center">
                                                            <MDBox mr={2} sx={{borderWidth: 1, borderColor: "#d2d6da", borderStyle: 'solid', borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                                                <PersonIcon color="white"/>
                                                            </MDBox>
                                                            <MDBox>
                                                                <MDTypography variant="body2" color="text" fontWeight="regular" sx={{fontSize: 14}}>Name</MDTypography>
                                                                <MDTypography variant="h6" fontWeight={"medium"} sx={{fontSize: 16}}>{selectedMember.name}</MDTypography>
                                                            </MDBox>
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item xs={12} lg={6}>
                                                        <MDBox mb={2} display="flex" alignItems="center">
                                                            <MDBox mr={2} sx={{borderWidth: 1, borderColor: "#d2d6da", borderStyle: 'solid', borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                                                {
                                                                    !moment(selectedMember.end_at).isSameOrAfter(moment(), "day") ?
                                                                        <AccessTimeIcon color="secondary" /> :
                                                                        <CheckCircleIcon color="success" />
                                                                }
                                                            </MDBox>
                                                            <MDBox>
                                                                <MDTypography variant="body2" color="text" fontWeight="regular" sx={{fontSize: 14}}>Status</MDTypography>
                                                                <MDTypography variant="h6" fontWeight={"medium"} sx={{fontSize: 16}}>{!moment(selectedMember.end_at).isSameOrAfter(moment(), "day") ? 'Expired' : 'Valid'}</MDTypography>
                                                            </MDBox>
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item xs={12} lg={6}>
                                                        <MDBox>
                                                            <MDTypography variant="body2" color="text" fontWeight="regular" sx={{fontSize: 14}}>Joined</MDTypography>
                                                            <MDTypography variant="h6" fontWeight={"medium"} sx={{fontSize: 16}}>{moment(selectedMember.start_at).format('MMM D, YYYY')}</MDTypography>
                                                        </MDBox>
                                                    </Grid>
                                                    <Grid item xs={12} lg={6}>
                                                        <MDBox>
                                                            <MDTypography variant="body2" color="text" fontWeight="regular" sx={{fontSize: 14}}>Valid Through</MDTypography>
                                                            <MDTypography variant="h6" fontWeight={"medium"} sx={{fontSize: 16}}>{moment(selectedMember.end_at).format('MMM D, YYYY')}</MDTypography>
                                                        </MDBox>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </MDBox>
                                    </Card>
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <MDBox mb={3}>
                                    <Card id="member-check-in" sx={{padding: 2}}>
                                        {
                                            !moment(selectedMember.end_at).isSameOrAfter(moment(), "day") ?
                                                <Formik
                                                    initialValues={paymentInitialValues}
                                                    validationSchema={paymentValidations}
                                                    onSubmit={handlePayment}
                                                >
                                                    {({values, errors, touched, isSubmitting}) => (
                                                        <Form id={paymentForm.formId} autoComplete="off">
                                                            <MDBox p={3}>
                                                                <MDTypography variant="h5">New Payment</MDTypography>
                                                                <MDTypography variant="body2">Enter the new payment's details.</MDTypography>
                                                            </MDBox>
                                                            <MDBox pb={3} px={3}>
                                                                <NewPaymentForm formData={{values, touched, formField: paymentForm.formField, errors}} />
                                                                <MDBox mt={2} width="100%">
                                                                    <MDBox>
                                                                        <MDButton
                                                                            disabled={isSubmitting}
                                                                            variant="gradient"
                                                                            color="secondary"
                                                                            type="submit"
                                                                            fullWidth
                                                                        >
                                                                            {isSubmitting &&
                                                                                <CircularProgress size={18} color="white"
                                                                                                  sx={{marginRight: 1}}/>}
                                                                            Submit & Check In
                                                                        </MDButton>
                                                                    </MDBox>
                                                                </MDBox>
                                                            </MDBox>
                                                        </Form>
                                                    )}
                                                </Formik> :
                                                <MDBox>
                                                    <MDBox p={2}>
                                                        <MDTypography variant="h5">Valid Membership</MDTypography>
                                                        <MDTypography variant="body2">The member is good to go. Check him in to continue.</MDTypography>
                                                        <MDButton
                                                            sx={{marginTop: 3}}
                                                            variant="gradient"
                                                            color="secondary"
                                                            type="submit"
                                                            fullWidth
                                                            onClick={handleCheckIn}
                                                        >
                                                            Check In
                                                        </MDButton>
                                                    </MDBox>
                                                </MDBox>
                                        }
                                    </Card>
                                </MDBox>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
        </BaseLayout>
    );
};

export default CheckIn;
